// src/pages/ConfirmBooking/core.jsx
import { useMemo, useState, useCallback, memo, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// import BookingSteps from "../components/BookingSteps";
import apiClient from "../../api"; // ⬅️ note: path changed because we're now in /ConfirmBooking/
import useSeatLockBackGuard from "../../hooks/useSeatLockBackGuard";
import useSeatLockCleanup from "../../hooks/useSeatLockCleanup";

// ✅ no-op replacement so existing JSX does not change
const BookingSteps = () => null;

/* ---------------- Matte palette ---------------- */
const PALETTE = {
  primary: "#C74A50",
  primaryDark: "#B93D45",
  primarySoft: "#FCEDEE",
  primaryRing: "rgba(199,74,80,0.12)",
  bg: "linear-gradient(180deg, #FBF8F8 0%, #F6F3F5 24%, #F1F2F7 62%, #EEF1F6 100%)",
  surface: "#FFFFFF",
  surfaceAlt: "#FCFCFD",
  border: "#E5E7EB",
  borderSoft: "#EEF1F4",
  text: "#1A1A1A",
  textSubtle: "#6B7280",
  textMuted: "#9CA3AF",
  pillBg: "#F3F4F6",
  violet: "#6D5BD0",
  violetBg: "#F4F1FF",
  pink: "#E05B88",
  pinkBg: "#FFF0F5",
  datePillBg: "#FFF9DB",
  acPillBg: "#EAF5FF",
  seatPillBg: "#FFE9EC",
  timeGreenBg: "#ECFDF5",
  shadowSm: "0 1px 2px rgba(16,24,40,0.04)",
  shadowMd: "0 8px 24px rgba(16,24,40,0.06)",
};

/* ---------------- Helpers ---------------- */
const getNiceDate = (dateStr, time) => {
  try {
    const d = new Date(dateStr);
    const ds = d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    return time ? `${ds} at ${time}` : ds;
  } catch {
    return dateStr || "--";
  }
};

// ✅ Stable clientId (guest + logged-in) for seat-lock ownership
const getClientId = () => {
  const key = "rb_client_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id =
      crypto?.randomUUID?.() ||
      `cid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(key, id);
  }
  return id;
};

const getStoredAuthToken = () =>
  localStorage.getItem("token") || localStorage.getItem("authToken") || "";

const SectionCard = ({ title, children }) => (
  <div
    className="rounded-3xl p-4 md:p-5 mt-4"
    style={{
      background: PALETTE.surface,
      border: `1px solid ${PALETTE.borderSoft}`,
      boxShadow: PALETTE.shadowSm,
    }}
  >
    {title ? (
      <div className="mb-3 pb-2" style={{ borderBottom: `1px solid ${PALETTE.borderSoft}` }}>
        <h3
          className="text-lg font-semibold"
          style={{ color: PALETTE.text, letterSpacing: "-0.01em" }}
        >
          {title}
        </h3>
      </div>
    ) : null}
    {children}
  </div>
);

const Label = ({ children }) => (
  <span
    className="block text-[11px] font-semibold mb-1.5 uppercase tracking-[0.06em]"
    style={{ color: PALETTE.textSubtle }}
  >
    {children}
  </span>
);

const Pill = ({ children }) => (
  <span
    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
    style={{
      background: PALETTE.pillBg,
      color: PALETTE.text,
      border: `1px solid ${PALETTE.borderSoft}`,
    }}
  >
    {children}
  </span>
);

const SoftPill = ({ children, bg }) => (
  <span
    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
    style={{
      background: bg,
      color: PALETTE.text,
      border: `1px solid ${PALETTE.borderSoft}`,
    }}
  >
    {children}
  </span>
);
const DatePill = ({ children }) => (
  <SoftPill bg={PALETTE.datePillBg}>{children}</SoftPill>
);
const AcPill = ({ children }) => (
  <SoftPill bg={PALETTE.acPillBg}>{children}</SoftPill>
);
const SeatPill = ({ children }) => (
  <span
    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
    style={{ background: PALETTE.primarySoft, color: PALETTE.primary, border: `1px solid rgba(199,74,80,0.12)` }}
  >
    {children}
  </span>
);
const TimeGreenPill = ({ children }) => (
  <SoftPill bg={PALETTE.timeGreenBg}>{children}</SoftPill>
);

const GenderSeatPill = ({ gender, children }) => {
  const isMale = gender === "M";
  const bg = isMale ? PALETTE.violetBg : PALETTE.pinkBg;
  const fg = isMale ? PALETTE.violet : PALETTE.pink;
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{
        background: bg,
        color: fg,
        border: `1px solid ${isMale ? "rgba(109,91,208,0.18)" : "rgba(224,91,136,0.18)"}`,
      }}
    >
      {children}
    </span>
  );
};

/** Query server for remaining hold time (cache-safe), return {ms, expiresAt} */
async function fetchHoldRemaining({
  busId,
  date,
  departureTime,
  seats,
  clientId,
}) {
  const seatsCsv = Array.isArray(seats) ? seats.map(String).join(",") : seats;
  const params = {
    busId,
    date,
    departureTime,
    seats: seatsCsv,
    t: Date.now(), // cache-buster
  };

  const cid = clientId || getClientId();

  try {
    const r1 = await apiClient.get("/bookings/lock-remaining", {
      params,
      headers: { "x-client-id": cid },
    });
    return {
      ms: r1?.data?.remainingMs ?? r1?.data?.ms ?? null,
      expiresAt: r1?.data?.expiresAt ?? null,
      headers: r1?.headers,
    };
  } catch {
    const r2 = await apiClient.get("/bookings/lock/remaining", {
      params,
      headers: { "x-client-id": cid },
    });
    return {
      ms: r2?.data?.remainingMs ?? r2?.data?.ms ?? null,
      expiresAt: r2?.data?.expiresAt ?? null,
      headers: r2?.headers,
    };
  }
}

/** Get best-effort server time (to reduce local clock drift) */
function serverNowFromHeaders(headers) {
  const h = headers?.date;
  const t = h ? Date.parse(h) : NaN;
  return Number.isFinite(t) ? t : Date.now();
}

// --- Live hold countdown (15 min seat lock) ---
const HoldCountdown = ({
  busId,
  date,
  departureTime,
  seats,
  clientId,
  lockVersion,
  onExpire,
}) => {
  const seatsKey = Array.isArray(seats)
    ? seats.map(String).join(",")
    : String(seats || "");

  const [remainingMs, setRemainingMs] = useState(() => {
    try {
      const raw = sessionStorage.getItem("rb_hold_expiry_preview");
      if (!raw) return null;

      const saved = JSON.parse(raw);
      const sameFlow =
        saved?.busId === busId &&
        saved?.date === date &&
        saved?.departureTime === departureTime &&
        saved?.seatsKey === seatsKey;

      if (!sameFlow) return null;

      const left = Math.max(0, Number(saved?.expiryAt || 0) - Date.now());
      return left > 0 ? left : null;
    } catch {
      return null;
    }
  });

  const onExpireRef = useRef(onExpire);
  useEffect(() => {
    onExpireRef.current = onExpire;
  }, [onExpire]);
  const expiryRef = useRef(null);
  const timerRef = useRef(null);
  const retryRef = useRef(0);

  useEffect(() => {
    let cancelled = false;

    const persistExpiryPreview = (expiryAt) => {
      try {
        sessionStorage.setItem(
          "rb_hold_expiry_preview",
          JSON.stringify({
            busId,
            date,
            departureTime,
            seatsKey,
            expiryAt,
          })
        );
      } catch {}
    };

    const startTicking = () => {
      if (timerRef.current) clearInterval(timerRef.current);
      const tick = () => {
        const left = Math.max(
          0,
          (expiryRef.current ?? Date.now()) - Date.now()
        );
        setRemainingMs(left);
        if (left <= 0) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          onExpireRef.current && onExpireRef.current();
        }
      };
      tick();
      timerRef.current = setInterval(tick, 1000);
    };

    const init = async () => {
      try {
        const { ms, expiresAt, headers } = await fetchHoldRemaining({
          busId,
          date,
          departureTime,
          seats,
          clientId,
        });
        const nowServer = serverNowFromHeaders(headers);
        const target = expiresAt
          ? new Date(expiresAt).getTime()
          : ms != null
          ? nowServer + Math.max(0, Number(ms))
          : nowServer;

        if (!cancelled && target <= nowServer + 500 && retryRef.current < 1) {
          retryRef.current += 1;
          await new Promise((r) => setTimeout(r, 700));
          if (cancelled) return;

          const again = await fetchHoldRemaining({
            busId,
            date,
            departureTime,
            seats,
            clientId,
          });
          const now2 = serverNowFromHeaders(again.headers);
          const target2 = again.expiresAt
            ? new Date(again.expiresAt).getTime()
            : again.ms != null
            ? now2 + Math.max(0, Number(again.ms))
            : now2;

          if (cancelled) return;
          expiryRef.current = target2;
          persistExpiryPreview(target2);
          startTicking();
          return;
        }

        if (cancelled) return;
        expiryRef.current = target;
        persistExpiryPreview(target);
        startTicking();
      } catch {
        expiryRef.current = Date.now();
        startTicking();
      }
    };

    const onVisibility = () => {
      if (document.hidden) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      } else if (!timerRef.current && expiryRef.current) {
        startTicking();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    init();

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [busId, date, departureTime, seatsKey, clientId, lockVersion, seats]);

  if (remainingMs == null) {
    return (
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
        style={{ background: PALETTE.seatPillBg, color: PALETTE.primary }}
      >
        Securing seats...
      </span>
    );
  }

  const total = Math.ceil(remainingMs / 1000);
  const mm = String(Math.floor(total / 60)).padStart(2, "0");
  const ss = String(total % 60).padStart(2, "0");
  const expired = total <= 0;

  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-extrabold tabular-nums"
      style={{
        background: expired ? "#FEE2E2" : PALETTE.primarySoft,
        color: expired ? "#991B1B" : PALETTE.primary,
        border: `1px solid ${expired ? "#FECACA" : "rgba(199,74,80,0.12)"}`,
      }}
      title="Your reserved seats are held for 15 minutes"
    >
      {expired ? "Hold expired" : `Hold: ${mm}:${ss}`}
    </span>
  );
};

const RowInput = ({
  id,
  name,
  label,
  type = "text",
  value,
  onChange,
  autoComplete,
  inputMode,
  enterKeyHint,
  placeholder,
  required,
  onBlur,
  error,
  maxLength,
  pattern,
}) => (
  <div className="w-full">
    <Label>{label}</Label>
    <input
      id={id}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      autoComplete={autoComplete}
      autoCapitalize="off"
      autoCorrect="off"
      inputMode={inputMode}
      enterKeyHint={enterKeyHint}
      placeholder={placeholder}
      required={required}
      maxLength={maxLength}
      pattern={pattern}
      aria-invalid={!!error}
      className="w-full bg-white px-3.5 py-3 rounded-2xl border outline-none transition"
      style={{
        borderColor: error ? "#DC2626" : PALETTE.border,
        color: PALETTE.text,
        fontSize: 16,
        boxShadow: error ? "0 0 0 3px rgba(220,38,38,0.08)" : "inset 0 1px 2px rgba(16,24,40,0.02)",
      }}
    />
    {error ? (
      <p className="mt-1 text-xs font-medium" style={{ color: "#B91C1C" }}>
        {error}
      </p>
    ) : null}
  </div>
);

const PassengerRow = memo(function PassengerRow({
  p,
  index,
  onName,
  onAge,
  onGender,
  errorsForSeat,
  onBlurName,
  onBlurAge,
}) {
  return (
    <div
      className="p-4 md:p-5 rounded-3xl"
      style={{
        background: "linear-gradient(180deg, #FFFFFF 0%, #FCFCFD 100%)",
        border: `1px solid ${PALETTE.borderSoft}`,
        boxShadow: PALETTE.shadowSm,
      }}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p
            className="font-semibold"
            style={{ color: PALETTE.text, letterSpacing: "-0.01em" }}
          >
            Passenger {index + 1}
          </p>
          <p className="text-xs mt-0.5" style={{ color: PALETTE.textSubtle }}>
            Enter passenger details exactly as required for travel.
          </p>
        </div>
        <GenderSeatPill gender={p.gender}>Seat {p.seat}</GenderSeatPill>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mt-4">
        <div className="md:col-span-2">
          <RowInput
            id={`p-name-${p.seat}`}
            name={`p-name-${p.seat}`}
            label="Name"
            value={p.name}
            onChange={(e) => onName(p.seat, e.target.value)}
            onBlur={() => onBlurName?.(p.seat)}
            autoComplete="name"
            enterKeyHint="next"
            placeholder="e.g., Ramesh Perera"
            required
            error={errorsForSeat?.name}
          />
        </div>
        <div className="md:col-span-1">
          <RowInput
            id={`p-age-${p.seat}`}
            name={`p-age-${p.seat}`}
            label="Age"
            type="number"
            value={p.age}
            onChange={(e) => onAge(p.seat, e.target.value)}
            onBlur={() => onBlurAge?.(p.seat)}
            inputMode="numeric"
            enterKeyHint="next"
            placeholder="e.g., 28"
            error={errorsForSeat?.age}
          />
        </div>
        <div className="md:col-span-2">
          <Label>Gender</Label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => onGender(p.seat, "M")}
              className="py-2.5 rounded-2xl border text-sm font-semibold transition"
              style={{
                borderColor:
                  p.gender === "M"
                    ? PALETTE.violet
                    : errorsForSeat?.gender
                    ? "#DC2626"
                    : PALETTE.border,
                background: p.gender === "M" ? PALETTE.violetBg : "#FFFFFF",
                color: p.gender === "M" ? PALETTE.violet : PALETTE.text,
                boxShadow: p.gender === "M" ? "0 0 0 3px rgba(109,91,208,0.08)" : "none",
              }}
            >
              Male
            </button>
            <button
              type="button"
              onClick={() => onGender(p.seat, "F")}
              className="py-2.5 rounded-2xl border text-sm font-semibold transition"
              style={{
                borderColor:
                  p.gender === "F"
                    ? PALETTE.pink
                    : errorsForSeat?.gender
                    ? "#DC2626"
                    : PALETTE.border,
                background: p.gender === "F" ? PALETTE.pinkBg : "#FFFFFF",
                color: p.gender === "F" ? PALETTE.pink : PALETTE.text,
                boxShadow: p.gender === "F" ? "0 0 0 3px rgba(224,91,136,0.08)" : "none",
              }}
            >
              Female
            </button>
          </div>
          {errorsForSeat?.gender ? (
            <p className="mt-1 text-xs font-medium" style={{ color: "#B91C1C" }}>
              {errorsForSeat.gender}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
});

export function useConfirmBookingCore() {
  const location = useLocation();
  const navigate = useNavigate();
  const pageTopRef = useRef(null);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const showLeaveConfirmRef = useRef(false);
  const leavingFlowRef = useRef(false);
  const backTrapActiveRef = useRef(false);
  const backGuardStateRef = useRef({
    rb_confirm_guard: true,
    id: `guard_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  });

  useEffect(() => {
    showLeaveConfirmRef.current = showLeaveConfirm;
  }, [showLeaveConfirm]);

  const phParams = new URLSearchParams(location.search || "");
  const phStatus = phParams.get("status_code") || phParams.get("status") || "";
  const cameBackFromGateway =
    !!phStatus && phStatus !== "2" && !/^success$/i.test(phStatus || "");

  useEffect(() => {
    if (!location.state && cameBackFromGateway) {
      try {
        const raw = sessionStorage.getItem("rb_confirm_draft");
        if (raw) {
          const draft = JSON.parse(raw);
          navigate(location.pathname, { replace: true, state: draft });
        }
      } catch {}
    }
  }, [cameBackFromGateway, location.state, location.pathname, navigate]);

  useEffect(() => {
    if (!location.state && !cameBackFromGateway) {
      try {
        const raw = sessionStorage.getItem("rb_confirm_draft");
        if (raw) {
          const draft = JSON.parse(raw);
          navigate(location.pathname, { replace: true, state: draft });
        }
      } catch {}
    }
  }, [location.state, cameBackFromGateway, location.pathname, navigate]);

  useEffect(() => {
    if (
      location.state &&
      !(location.state.formDraft && location.state.passengersDraft)
    ) {
      try {
        const raw = sessionStorage.getItem("rb_confirm_draft");
        if (raw) {
          const draft = JSON.parse(raw);
          const merged = {
            ...location.state,
            formDraft: location.state.formDraft || draft.formDraft,
            passengersDraft:
              location.state.passengersDraft || draft.passengersDraft,
            seatGenders: location.state.seatGenders || draft.seatGenders,
          };
          const changed =
            merged.formDraft !== location.state.formDraft ||
            merged.passengersDraft !== location.state.passengersDraft ||
            merged.seatGenders !== location.state.seatGenders;
          if (changed) {
            navigate(location.pathname, { replace: true, state: merged });
          }
        }
      } catch {}
    }
  }, [location.state, location.pathname, navigate]);

  const {
    bus,
    selectedSeats,
    date,
    totalPrice,
    priceDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    departureTime,
    seatGenders,
    alreadyLocked,
    clientId,
    restoreFromConfirm,
    fromLoginRedirect,
    formDraft,
    passengersDraft,
  } = location.state || {};

  const prices = useMemo(() => {
    const base =
      priceDetails?.basePrice ??
      (typeof totalPrice === "number" ? totalPrice : 0);
    const fee = priceDetails?.convenienceFee ?? 0;
    const tot = priceDetails?.totalPrice ?? totalPrice ?? base + fee;
    return {
      basePrice: Number(base) || 0,
      convenienceFee: Number(fee) || 0,
      total: Number(tot) || 0,
    };
  }, [priceDetails, totalPrice]);

  const [form, setForm] = useState({
    name: formDraft?.name || "",
    mobile: formDraft?.mobile || "",
    nic: formDraft?.nic || "",
    email: formDraft?.email || "",
  });
  const onChangeForm = useCallback((e) => {
    const { name, value } = e.target;
    setForm((prev) =>
      prev[name] === value ? prev : { ...prev, [name]: value }
    );
  }, []);

  const profilePrefilledRef = useRef(false);

  const formatPhoneForDisplay = (value = "") => {
    const cleaned = String(value).replace(/\s+/g, "").replace(/-/g, "");

    if (/^\+94\d{9}$/.test(cleaned)) {
      return `0${cleaned.slice(3)}`;
    }

    if (/^94\d{9}$/.test(cleaned)) {
      return `0${cleaned.slice(2)}`;
    }

    return cleaned;
  };

  useEffect(() => {
    if (profilePrefilledRef.current) return;

    const token = getStoredAuthToken();
    if (!token) return;

    let cancelled = false;

    const prefillFromProfile = async () => {
      try {
        const { data } = await apiClient.get("/profile");

        if (cancelled || !data) return;

        setForm((prev) => {
          const next = {
            ...prev,
            name: prev.name || data.fullName || "",
            mobile: prev.mobile || formatPhoneForDisplay(data.phone || ""),
            nic: prev.nic || data.nic || "",
            email: prev.email || data.email || "",
          };

          const changed =
            next.name !== prev.name ||
            next.mobile !== prev.mobile ||
            next.nic !== prev.nic ||
            next.email !== prev.email;

          return changed ? next : prev;
        });

        profilePrefilledRef.current = true;
      } catch {}
    };

    prefillFromProfile();

    return () => {
      cancelled = true;
    };
  }, []);

  const initialPassengers = useMemo(
    () =>
      passengersDraft && Array.isArray(passengersDraft)
        ? passengersDraft
        : (selectedSeats || []).map((seatNo) => ({
            seat: String(seatNo),
            name: "",
            age: "",
            gender: seatGenders?.[String(seatNo)] === "F" ? "F" : "M",
          })),
    [selectedSeats, seatGenders, passengersDraft]
  );
  const [passengers, setPassengers] = useState(initialPassengers);

  useEffect(() => {
    setPassengers((prev) => {
      const prevSeats = Array.isArray(prev)
        ? prev.map((p) => String(p.seat)).join(",")
        : "";
      const nextSeats = Array.isArray(initialPassengers)
        ? initialPassengers.map((p) => String(p.seat)).join(",")
        : "";

      if (
        prevSeats === nextSeats &&
        prev.length === initialPassengers.length
      ) {
        return prev;
      }

      return initialPassengers;
    });
  }, [initialPassengers]);

  const setPassengerName = useCallback((seat, name) => {
    setPassengers((prev) => {
      const i = prev.findIndex((x) => x.seat === String(seat));
      if (i === -1 || prev[i].name === name) return prev;
      const next = prev.slice();
      next[i] = { ...next[i], name };
      return next;
    });
  }, []);

  const setPassengerAge = useCallback((seat, age) => {
    setPassengers((prev) => {
      const i = prev.findIndex((x) => x.seat === String(seat));
      if (i === -1 || prev[i].age === age) return prev;
      const next = prev.slice();
      next[i] = { ...next[i], age };
      return next;
    });
  }, []);

  const setPassengerGender = useCallback((seat, gender) => {
    setPassengers((prev) => {
      const i = prev.findIndex((x) => x.seat === String(seat));
      if (i === -1 || prev[i].gender === gender) return prev;
      const next = prev.slice();
      next[i] = { ...next[i], gender };
      return next;
    });
  }, []);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [holdExpired, setHoldExpired] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState({
    name: "",
    mobile: "",
    nic: "",
    email: "",
    terms: "",
    passengers: {},
  });

  const selectedSeatStrings = useMemo(
    () => (selectedSeats || []).map(String),
    [selectedSeats]
  );

  const { releaseSeats, suppressAutoRelease } = useSeatLockCleanup({
    busId: bus?._id,
    date,
    departureTime,
    seats: selectedSeatStrings,
  });

  const writePreselectBundle = useCallback((payload) => {
    try {
      const ttlMs = 3 * 60 * 1000;
      const meta = { token: payload.token, expiresAt: Date.now() + ttlMs };
      sessionStorage.setItem("rb_preselect_seats", JSON.stringify(payload));
      sessionStorage.setItem("rb_preselect_meta", JSON.stringify(meta));
    } catch {}
  }, []);

  const goBackToResults = useCallback(async () => {
    if (leavingFlowRef.current) return;
    leavingFlowRef.current = true;
    const seatStr = (selectedSeats || []).map(String);
    const seatNum = (selectedSeats || []).map((s) => Number(s));

    const token =
      String(Date.now()) + "-" + Math.random().toString(36).slice(2, 8);
    const restorePayload = {
      restoreFromConfirm: true,
      token,
      bus,
      busId: bus?._id,
      date,
      departureTime,
      selectedSeats: seatStr,
      selectedSeatsNums: seatNum,
      selectedBoardingPoint,
      selectedDroppingPoint,
      seatGenders: seatGenders || {},
      priceDetails: prices,
      formDraft: { ...form },
      passengersDraft: passengers,
      clientId: clientId || getClientId(),
    };

    try {
      sessionStorage.setItem("rb_confirm_draft", JSON.stringify(restorePayload));
      sessionStorage.setItem("rb_restore_from_confirm", "1");
      sessionStorage.setItem("rb_restore_payload", JSON.stringify(restorePayload));
      writePreselectBundle({
        token,
        busId: bus?._id,
        date,
        departureTime,
        seats: seatStr,
        seatsNums: seatNum,
        seatGenders: seatGenders || {},
      });
    } catch {}

    try {
      await Promise.resolve(releaseSeats?.());
    } catch {}

    try {
      sessionStorage.removeItem("rb_skip_release_on_unmount");
    } catch {}

    const searchParams = new URLSearchParams({
      from: bus?.from || "",
      to: bus?.to || "",
      date: date || "",
      preselect: "1",
      token,
    }).toString();

    navigate(`/search-results?${searchParams}`, {
      replace: true,
      state: restorePayload,
    });
  }, [
    releaseSeats,
    bus,
    date,
    departureTime,
    selectedSeats,
    selectedBoardingPoint,
    selectedDroppingPoint,
    seatGenders,
    prices,
    navigate,
    form,
    passengers,
    writePreselectBundle,
    clientId,
  ]);

  const backToResultsAndRelease = useCallback(async () => {
    if (leavingFlowRef.current) return;
    leavingFlowRef.current = true;
    const seatStr = (selectedSeats || []).map(String);
    const seatNum = (selectedSeats || []).map((s) => Number(s));

    const token =
      String(Date.now()) + "-" + Math.random().toString(36).slice(2, 8);

    const restorePayload = {
      restoreFromConfirm: true,
      token,
      bus,
      busId: bus?._id,
      date,
      departureTime,
      selectedSeats: seatStr,
      selectedSeatsNums: seatNum,
      selectedBoardingPoint,
      selectedDroppingPoint,
      seatGenders: seatGenders || {},
      priceDetails: prices,
      formDraft: { ...form },
      passengersDraft: passengers,
      clientId: clientId || getClientId(),
    };

    try {
      sessionStorage.setItem("rb_confirm_draft", JSON.stringify(restorePayload));
      sessionStorage.setItem("rb_restore_from_confirm", "1");
      sessionStorage.setItem("rb_restore_payload", JSON.stringify(restorePayload));
      writePreselectBundle({
        token,
        busId: bus?._id,
        date,
        departureTime,
        seats: seatStr,
        seatsNums: seatNum,
        seatGenders: seatGenders || {},
      });
    } catch {}

    try {
      await Promise.resolve(releaseSeats?.());
    } catch {}

    try {
      sessionStorage.removeItem("rb_skip_release_on_unmount");
    } catch {}

    const searchParams = new URLSearchParams({
      from: bus?.from || "",
      to: bus?.to || "",
      date: date || "",
      preselect: "1",
      token,
    }).toString();

    navigate(`/search-results?${searchParams}`, {
      replace: true,
      state: restorePayload,
    });
  }, [
    releaseSeats,
    bus,
    date,
    departureTime,
    selectedSeats,
    selectedBoardingPoint,
    selectedDroppingPoint,
    seatGenders,
    prices,
    navigate,
    form,
    passengers,
    writePreselectBundle,
    clientId,
  ]);

  const cancelBookingAndGoHome = useCallback(async () => {
    if (leavingFlowRef.current) return;
    leavingFlowRef.current = true;
    try {
      await Promise.resolve(releaseSeats?.());
    } catch {}

    try {
      sessionStorage.removeItem("rb_confirm_draft");
      sessionStorage.removeItem("rb_restore_from_confirm");
      sessionStorage.removeItem("rb_restore_payload");
      sessionStorage.removeItem("rb_preselect_seats");
      sessionStorage.removeItem("rb_preselect_meta");
      sessionStorage.removeItem("rb_skip_release_on_unmount");
      sessionStorage.removeItem("rb_hold_expiry_preview");
    } catch {}

    navigate("/");
  }, [releaseSeats, navigate]);

  const openLeaveConfirm = useCallback(() => {
    setShowLeaveConfirm(true);
  }, []);

  const closeLeaveConfirm = useCallback(() => {
    setShowLeaveConfirm(false);

    if (backTrapActiveRef.current && !leavingFlowRef.current) {
      try {
        window.history.pushState(backGuardStateRef.current, "");
      } catch {}
      backTrapActiveRef.current = false;
    }
  }, []);

  const openLoginModal = useCallback(() => {
    setShowLoginModal(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  const handleLoginSuccess = useCallback(() => {
    setShowLoginModal(false);

    try {
      const raw = sessionStorage.getItem("rb_confirm_draft");
      if (raw) {
        const draft = JSON.parse(raw);

        navigate("/confirm-booking", {
          replace: true,
          state: {
            ...draft,
            restoreFromConfirm: true,
            fromLoginRedirect: true,
          },
        });
      }
    } catch {}
  }, [navigate]);

  const releaseSeatsKeepalive = useCallback(() => {
    try {
      if (!bus?._id || !date || !departureTime || !selectedSeatStrings.length)
        return;

      const token = getStoredAuthToken();
      const baseURL =
        (apiClient && apiClient.defaults && apiClient.defaults.baseURL) || "";
      const url = baseURL
        ? `${baseURL.replace(/\/+$/, "")}/bookings/release`
        : "/bookings/release";

      const payload = {
        busId: bus._id,
        date,
        departureTime,
        seats: selectedSeatStrings.map(String),
        clientId: clientId || getClientId(),
      };

      fetch(url, {
        method: "DELETE",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });
    } catch {}
  }, [bus?._id, date, departureTime, selectedSeatStrings, clientId]);


  const confirmLeaveToResults = useCallback(async () => {
    if (leavingFlowRef.current) return;

    const shouldUseNativeBackToResults = backTrapActiveRef.current;

    leavingFlowRef.current = true;

    setShowLeaveConfirm(false);
    backTrapActiveRef.current = false;

    const seatStr = (selectedSeats || []).map(String);
    const seatNum = (selectedSeats || []).map((s) => Number(s));
    const token =
      String(Date.now()) + "-" + Math.random().toString(36).slice(2, 8);

    const restorePayload = {
      restoreFromConfirm: true,
      token,
      bus,
      busId: bus?._id,
      date,
      departureTime,
      selectedSeats: seatStr,
      selectedSeatsNums: seatNum,
      selectedBoardingPoint,
      selectedDroppingPoint,
      seatGenders: seatGenders || {},
      priceDetails: prices,
      formDraft: { ...form },
      passengersDraft: passengers,
      clientId: clientId || getClientId(),
    };

    try {
      sessionStorage.setItem("rb_confirm_draft", JSON.stringify(restorePayload));
      sessionStorage.setItem("rb_restore_from_confirm", "1");
      sessionStorage.setItem("rb_restore_payload", JSON.stringify(restorePayload));
      writePreselectBundle({
        token,
        busId: bus?._id,
        date,
        departureTime,
        seats: seatStr,
        seatsNums: seatNum,
        seatGenders: seatGenders || {},
      });
    } catch {}

    try {
      await Promise.resolve(releaseSeats?.());
    } catch {
      releaseSeatsKeepalive();
    }

    try {
      sessionStorage.removeItem("rb_skip_release_on_unmount");
      sessionStorage.removeItem("rb_hold_expiry_preview");
    } catch {}

    const searchParams = new URLSearchParams({
      from: bus?.from || "",
      to: bus?.to || "",
      date: date || "",
      preselect: "1",
      token,
    }).toString();

    if (shouldUseNativeBackToResults && window.history.length > 1) {
      window.history.back();
      return;
    }

    navigate(`/search-results?${searchParams}`, {
      replace: true,
      state: restorePayload,
    });
  }, [
    bus,
    date,
    departureTime,
    selectedSeats,
    selectedBoardingPoint,
    selectedDroppingPoint,
    seatGenders,
    prices,
    form,
    passengers,
    clientId,
    releaseSeats,
    releaseSeatsKeepalive,
    writePreselectBundle,
    navigate,
  ]);

  const verifyHoldAlive = useCallback(async () => {
    try {
      const { ms, expiresAt, headers } = await fetchHoldRemaining({
        busId: bus?._id,
        date,
        departureTime,
        seats: selectedSeatStrings,
        clientId: clientId || getClientId(),
      });
      const now = serverNowFromHeaders(headers);

      if (expiresAt == null && ms == null) return false;

      const left = expiresAt
        ? new Date(expiresAt).getTime() - now
        : Number(ms) || 0;

      return left > 1500;
    } catch {
      return false;
    }
  }, [bus?._id, date, departureTime, selectedSeatStrings, clientId]);

  const handleHoldExpiredRedirect = useCallback(async () => {
    if (leavingFlowRef.current) return;
    leavingFlowRef.current = true;

    setHoldExpired(true);

    try {
      sessionStorage.setItem(
        "rb_search_results_notice",
        JSON.stringify({
          type: "error",
          message: "Seat hold expired. Please select your seats again.",
        })
      );
    } catch {}

    try {
      await Promise.resolve(releaseSeats?.());
    } catch {}

    try {
      sessionStorage.removeItem("rb_confirm_draft");
      sessionStorage.removeItem("rb_restore_from_confirm");
      sessionStorage.removeItem("rb_restore_payload");
      sessionStorage.removeItem("rb_preselect_seats");
      sessionStorage.removeItem("rb_preselect_meta");
      sessionStorage.removeItem("rb_hold_expiry_preview");
      sessionStorage.removeItem("rb_skip_release_on_unmount");
      sessionStorage.removeItem("rb_back_from_gateway");
    } catch {}

    const searchParams = new URLSearchParams({
      from: bus?.from || "",
      to: bus?.to || "",
      date: date || "",
    }).toString();

    navigate(`/search-results?${searchParams}`, { replace: true });
  }, [releaseSeats, navigate, bus?.from, bus?.to, date]);

  const normalizeSriLankanPhone = (value = "") => {
    const cleaned = String(value).replace(/\s+/g, "").replace(/-/g, "");

    if (/^0\d{9}$/.test(cleaned)) {
      return `+94${cleaned.slice(1)}`;
    }

    if (/^94\d{9}$/.test(cleaned)) {
      return `+${cleaned}`;
    }

    if (/^\+94\d{9}$/.test(cleaned)) {
      return cleaned;
    }

    return null;
  };

  const phoneOk = (v) => !!normalizeSriLankanPhone(v);
  const emailOk = (v) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());
  const nonEmpty = (v) => String(v || "").trim().length > 1;

  const computePassengerErrors = useCallback((list) => {
    const pe = {};
    list.forEach((p) => {
      const e = {};
      if (!nonEmpty(p.name)) e.name = "Passenger name is required";
      if (p.age && Number(p.age) < 0) e.age = "Age must be positive";
      if (!p.gender) e.gender = "Please select gender";
      if (Object.keys(e).length) pe[p.seat] = e;
    });
    return pe;
  }, []);

  const validateAll = useCallback(() => {
    const next = {
      name: nonEmpty(form.name) ? "" : "Full name is required",
      mobile: phoneOk(form.mobile)
        ? ""
        : "Enter a valid mobile number (07XXXXXXXX or +947XXXXXXXX)",
      nic: nonEmpty(form.nic) ? "" : "NIC / Passport is required",
      email: emailOk(form.email) ? "" : "Enter a valid email address",
      terms: termsAccepted ? "" : "You must accept the Terms & Conditions",
      passengers: computePassengerErrors(passengers),
    };
    setErrors(next);

    const firstFieldId = next.name
      ? "name"
      : next.mobile
      ? "mobile"
      : next.nic
      ? "nic"
      : next.email
      ? "email"
      : Object.keys(next.passengers)[0]
      ? `p-name-${Object.keys(next.passengers)[0]}`
      : "";

    if (firstFieldId) {
      const el = document.getElementById(firstFieldId);
      if (el?.scrollIntoView)
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      if (el?.focus) setTimeout(() => el.focus(), 200);
      return false;
    }
    return true;
  }, [form, termsAccepted, passengers, computePassengerErrors]);

  const blurValidateField = useCallback(
    (field) => {
      setErrors((prev) => {
        const next = { ...prev };
        if (field === "name")
          next.name = nonEmpty(form.name) ? "" : "Full name is required";
        if (field === "mobile")
          next.mobile = phoneOk(form.mobile)
            ? ""
            : "Enter a valid mobile number (07XXXXXXXX or +947XXXXXXXX)";
        if (field === "nic")
          next.nic = nonEmpty(form.nic) ? "" : "NIC / Passport is required";
        if (field === "email")
          next.email = emailOk(form.email) ? "" : "Enter a valid email address";
        return next;
      });
    },
    [form]
  );

  const blurValidatePassenger = useCallback(
    (seat, field) => {
      setErrors((prev) => {
        const next = { ...prev, passengers: { ...prev.passengers } };
        const p = passengers.find((x) => x.seat === String(seat));
        const slot = { ...(next.passengers[seat] || {}) };
        if (field === "name")
          slot.name = nonEmpty(p?.name) ? "" : "Passenger name is required";
        if (field === "age" && p?.age && Number(p.age) < 0)
          slot.age = "Age must be positive";
        next.passengers[seat] = slot;
        if (!slot.name && !slot.age && !slot.gender)
          delete next.passengers[seat];
        return next;
      });
    },
    [passengers]
  );

  const toggleTerms = () => {
    setTermsAccepted((v) => {
      const nv = !v;
      setErrors((prev) => ({
        ...prev,
        terms: nv ? "" : "You must accept the Terms & Conditions",
      }));
      return nv;
    });
  };

  const [lockVersion, setLockVersion] = useState(0);
  const acquireOrRefreshSeatLock = useCallback(async () => {
    if (
      !bus?._id ||
      !date ||
      !departureTime ||
      selectedSeatStrings.length === 0
    )
      return;
    try {
      const cid = clientId || getClientId();
      await apiClient.post(
        "/bookings/lock",
        {
          busId: bus._id,
          date,
          departureTime,
          seats: selectedSeatStrings,
          clientId: cid,
        },
        { headers: { "x-client-id": cid } }
      );
      setHoldExpired(false);
      sessionStorage.setItem("rb_skip_release_on_unmount", "1");
      suppressAutoRelease?.();
      setLockVersion((v) => v + 1);
    } catch (e) {
      console.warn("Re-lock seats failed:", e?.response?.data || e?.message);
    }
  }, [bus, date, departureTime, selectedSeatStrings, suppressAutoRelease, clientId]);

  const cameFromGatewayFlag = (() => {
    try {
      return sessionStorage.getItem("rb_back_from_gateway") === "1";
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    const checkExistingHold = async () => {
      const alive = await verifyHoldAlive();

      if (alive) {
        setHoldExpired(false);
        setLockVersion((v) => v + 1);
        return;
      }

      if (fromLoginRedirect) {
        const expiredFlag =
          sessionStorage.getItem("rb_hold_expiry_preview") === "expired";
        if (!expiredFlag) {
          await acquireOrRefreshSeatLock();
          const aliveAfterRelock = await verifyHoldAlive();

          if (aliveAfterRelock) {
            setHoldExpired(false);
            setLockVersion((v) => v + 1);
            return;
          }
        }
      }

      setHoldExpired(true);
    };

    if (cameBackFromGateway || cameFromGatewayFlag || restoreFromConfirm) {
      checkExistingHold();
      try {
        sessionStorage.removeItem("rb_back_from_gateway");
      } catch {}
    }
  }, [
    cameBackFromGateway,
    cameFromGatewayFlag,
    restoreFromConfirm,
    fromLoginRedirect,
    verifyHoldAlive,
    acquireOrRefreshSeatLock,
  ]);

  const saveConfirmDraft = useCallback(() => {
    try {
      sessionStorage.setItem(
        "rb_confirm_draft",
        JSON.stringify({
          restoreFromConfirm: true,
          fromLoginRedirect: true,
          bus,
          selectedSeats,
          date,
          priceDetails,
          selectedBoardingPoint,
          selectedDroppingPoint,
          departureTime,
          seatGenders,
          clientId: clientId || getClientId(),
          formDraft: { ...form },
          passengersDraft: passengers,
        })
      );
    } catch {}
  }, [
    bus,
    selectedSeats,
    date,
    priceDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    departureTime,
    seatGenders,
    form,
    passengers,
    clientId,
  ]);

  useEffect(() => {
    saveConfirmDraft();
  }, [
    saveConfirmDraft,
    form,
    passengers,
    selectedSeats,
    date,
    priceDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    departureTime,
    seatGenders,
  ]);

  useEffect(() => {
    const handler = () => {
      try {
        saveConfirmDraft();
      } catch {}
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [saveConfirmDraft]);

  useEffect(() => {
    const onPageHide = () => {
      if (leavingFlowRef.current) return;
      if (showLeaveConfirmRef.current) {
        releaseSeatsKeepalive();
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden" && !leavingFlowRef.current) {
        if (showLeaveConfirmRef.current) {
          releaseSeatsKeepalive();
        }
      }
    };

    window.addEventListener("pagehide", onPageHide);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [releaseSeatsKeepalive]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        if (!validateAll()) {
          return;
        }

        if (holdExpired) {
          await handleHoldExpiredRedirect();
          return;
        }

        if (!termsAccepted) {
          alert("Please agree to the Terms & Conditions.");
          return;
        }

        const token = getStoredAuthToken();
        if (!token) {
          saveConfirmDraft();
          sessionStorage.setItem("rb_skip_release_on_unmount", "1");
          suppressAutoRelease?.();
          setShowLoginModal(true);
          return;
        }

        let stillHeld = await verifyHoldAlive();

        if (!stillHeld) {
          await acquireOrRefreshSeatLock();
          stillHeld = await verifyHoldAlive();
        }

        if (!stillHeld) {
          await handleHoldExpiredRedirect();
          return;
        }

        sessionStorage.setItem("rb_skip_release_on_unmount", "1");
        suppressAutoRelease();

        saveConfirmDraft();

        const payloadPassengers = passengers.map(
          ({ seat, name, age, gender }) => ({
            seat,
            name,
            age: age === "" ? undefined : Number(age),
            gender,
          })
        );

        const normalizedMobile = normalizeSriLankanPhone(form.mobile);

        if (!normalizedMobile) {
          setErrors((prev) => ({
            ...prev,
            mobile: "Enter a valid mobile number (07XXXXXXXX or +947XXXXXXXX)",
          }));
          return;
        }

        const { data: createRes } = await apiClient.post("/bookings", {
          busId: bus?._id,
          date,
          departureTime,
          passenger: {
            name: form?.name,
            email: form?.email,
            mobile: normalizedMobile,
            nic: form?.nic,
          },
          boardingPoint: selectedBoardingPoint,
          droppingPoint: selectedDroppingPoint,
          passengers: payloadPassengers,
          seatAllocations: payloadPassengers.map((p) => ({
            seat: p.seat,
            gender: p.gender,
          })),
        });

        const booking = createRes?.booking;
        if (!booking?.bookingNo) {
          alert("Could not create booking. Please try again.");
          return;
        }

        try {
          sessionStorage.setItem(
            "rb_ticket_payload",
            JSON.stringify({
              bookingDetails: {
                bookingNo: booking.bookingNo,
                bookingId: booking._id,
                bus,
                passengers: payloadPassengers,
                passenger: {
                  name: form?.name,
                  email: form?.email,
                  mobile: normalizedMobile,
                  nic: form?.nic,
                },
                date,
                departureTime,
                selectedSeats,
                priceDetails: {
                  basePrice: prices.basePrice,
                  convenienceFee: prices.convenienceFee,
                  totalPrice: prices.total,
                },
                boardingPoint: selectedBoardingPoint,
                droppingPoint: selectedDroppingPoint,
              },
            })
          );
        } catch {}

        const { data: genie } = await apiClient.post("/genie/create", {
          bookingNo: booking.bookingNo,
          amount: Number(prices.total || 0),
        });

        if (!genie?.paymentUrl) {
          alert("Payment gateway is unavailable. Please try again.");
          return;
        }

        window.location.href = genie.paymentUrl;
      } catch (err) {
        console.error("Proceed to Pay error:", err);
        alert(
          err?.response?.data?.message ||
            err?.message ||
            "Payment failed to initialize. Please try again."
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      bus,
      date,
      departureTime,
      form,
      passengers,
      selectedBoardingPoint,
      selectedDroppingPoint,
      prices,
      validateAll,
      holdExpired,
      termsAccepted,
      verifyHoldAlive,
      releaseSeats,
      suppressAutoRelease,
      acquireOrRefreshSeatLock,
      saveConfirmDraft,
      handleHoldExpiredRedirect,
    ]
  );

  const missingData =
    !bus ||
    !selectedSeats ||
    !date ||
    !selectedBoardingPoint ||
    !selectedDroppingPoint ||
    prices.total === undefined;

  useSeatLockBackGuard({
    enabled: false,
    busId: bus?._id,
    date,
    departureTime,
    seats: selectedSeatStrings,
    onConfirmBack: goBackToResults,
  });

  useEffect(() => {
    if (missingData || selectedSeatStrings.length === 0) return;

    try {
      window.history.pushState(backGuardStateRef.current, "");
    } catch {}

    const onPop = () => {
      if (leavingFlowRef.current) return;

      if (showLeaveConfirmRef.current) {
        try {
          window.history.pushState(backGuardStateRef.current, "");
        } catch {}
        return;
      }

      backTrapActiveRef.current = true;
      setShowLeaveConfirm(true);
    };

    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [missingData, selectedSeatStrings.length]);

  return {
    location,
    navigate,
    pageTopRef,

    bus,
    selectedSeats,
    date,
    totalPrice,
    priceDetails,
    selectedBoardingPoint,
    selectedDroppingPoint,
    departureTime,
    seatGenders,
    alreadyLocked,
    clientId,
    formDraft,
    passengersDraft,
    prices,
    form,
    passengers,
    errors,
    termsAccepted,
    holdExpired,
    lockVersion,
    isSubmitting,
    missingData,
    cameBackFromGateway,
    showLeaveConfirm,
    showLoginModal,

    onChangeForm,
    setForm,
    setPassengers,
    setPassengerName,
    setPassengerAge,
    setPassengerGender,
    setTermsAccepted,
    setHoldExpired,
    setErrors,
    releaseSeats,
    suppressAutoRelease,
    writePreselectBundle,
    goBackToResults,
    backToResultsAndRelease,
    cancelBookingAndGoHome,
    openLeaveConfirm,
    closeLeaveConfirm,
    openLoginModal,
    closeLoginModal,
    handleLoginSuccess,
    confirmLeaveToResults,
    verifyHoldAlive,
    handleHoldExpiredRedirect,
    releaseSeatsKeepalive,
    validateAll,
    blurValidateField,
    blurValidatePassenger,
    toggleTerms,
    acquireOrRefreshSeatLock,
    saveConfirmDraft,
    handleSubmit,
    selectedSeatStrings,
  };
}

export {
  PALETTE,
  BookingSteps,
  SectionCard,
  Label,
  DatePill,
  AcPill,
  SeatPill,
  TimeGreenPill,
  HoldCountdown,
  RowInput,
  PassengerRow,
  getNiceDate,
};





